// D98.12 - the in-page exchange-slip form that replaces five chained window.prompt calls.
// Props: onSubmit(answers) gets the SAME object askExchange returns today
//   { returnedItemName, returnedQty, deliveredItemName, deliveredQty, reason };
//   onCancel(); busy disables the buttons. Validation is askExchange's own (worksheetPrompts.ts),
//   fed from the fields in prompt order, so the rules and the refusal messages cannot drift.
import React from 'react';
import { askExchange, ExchangeAnswers } from './worksheetPrompts';

type Props = { onSubmit: (answers: ExchangeAnswers) => void; onCancel: () => void; busy?: boolean };

const input = 'h-8 w-full rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-primary-500';
const label = 'mb-1 block text-xs font-semibold text-slate-600';

export function ExchangeSlipForm({ onSubmit, onCancel, busy = false }: Props) {
  const [f, setF] = React.useState({ returnedItemName: '', returnedQty: '', deliveredItemName: '', deliveredQty: '', reason: '' });
  const [error, setError] = React.useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const answers = [f.returnedItemName, f.returnedQty, f.deliveredItemName, f.deliveredQty, f.reason];
    let next = 0;
    const result = askExchange(() => answers[next++] ?? null);
    if (result.kind === 'refused') return setError(result.message);
    if (result.kind === 'ok') {
      setError(null);
      onSubmit(result.value);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-slate-200 bg-white p-4" aria-label="Exchange slip">
      <div className="text-sm font-semibold text-slate-800">Exchange slip</div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className={label} htmlFor="ex-returned-name">Returned item name</label>
          <input id="ex-returned-name" className={input} value={f.returnedItemName} onChange={set('returnedItemName')} />
        </div>
        <div>
          <label className={label} htmlFor="ex-returned-qty">Returned qty</label>
          <input id="ex-returned-qty" type="number" min="0" className={input} value={f.returnedQty} onChange={set('returnedQty')} />
        </div>
        <div>
          <label className={label} htmlFor="ex-delivered-name">Delivered replacement item name</label>
          <input id="ex-delivered-name" className={input} value={f.deliveredItemName} onChange={set('deliveredItemName')} />
        </div>
        <div>
          <label className={label} htmlFor="ex-delivered-qty">Delivered qty</label>
          <input id="ex-delivered-qty" type="number" min="0" className={input} value={f.deliveredQty} onChange={set('deliveredQty')} />
        </div>
        <div className="md:col-span-2">
          <label className={label} htmlFor="ex-reason">Reason</label>
          <input id="ex-reason" className={input} value={f.reason} onChange={set('reason')} />
        </div>
      </div>
      {error && <div role="alert" className="text-xs text-rose-700">{error}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
          Cancel
        </button>
        <button type="submit" disabled={busy} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800">
          {busy ? 'Creating...' : 'Create exchange slip'}
        </button>
      </div>
    </form>
  );
}
